import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@shared/services/firebase';
import { SpinwheelCreditScore } from '@shared/types';
import { useLiveDataUid } from './useLiveDataUid';
import { logError } from '@/lib/log';

/**
 * Focused slice behind the DataContext facade: the user's Spinwheel credit
 * score, parsed out of the spinwheelUsers/{uid} document.
 *
 * NOTE: components should keep consuming useData() from DataContext for now —
 * demo mode (DemoProviders) only stubs the facade's DataContext.Provider, so a
 * component reading this context directly would crash under /demo.
 */
interface CreditScoreContextType {
    spinwheelCreditScore: SpinwheelCreditScore | null;
}

export const CreditScoreContext = createContext<CreditScoreContextType | undefined>(undefined);

export const useCreditScoreContext = () => {
    const context = useContext(CreditScoreContext);
    if (!context) {
        throw new Error('useCreditScoreContext must be used within a CreditScoreProvider');
    }
    return context;
};

export const CreditScoreProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const uid = useLiveDataUid();
    const [spinwheelCreditScore, setSpinwheelCreditScore] = useState<SpinwheelCreditScore | null>(null);

    useEffect(() => {
        if (!uid) {
            setSpinwheelCreditScore(null);
            return;
        }

        // Subscribe to Spinwheel credit score (document ID = uid)
        const spinwheelDocRef = doc(db, 'spinwheelUsers', uid);
        return onSnapshot(spinwheelDocRef, (snapshot) => {
            if (!snapshot.exists()) {
                setSpinwheelCreditScore(null);
                return;
            }
            const docData = snapshot.data();
            const data = docData?.data;
            if (!data) {
                setSpinwheelCreditScore(null);
                return;
            }
            // creditScoreDetails lives inside creditReports[0]
            const report = Array.isArray(data.creditReports) ? data.creditReports[0] : null;
            const details = Array.isArray(report?.creditScoreDetails) ? report.creditScoreDetails[0] : null;
            const score = details?.creditScore ?? report?.profile?.creditScore ?? data.profile?.creditScore;
            if (score != null) {
                setSpinwheelCreditScore({
                    creditScore: score,
                    modelName: details?.modelName ?? report?.profile?.modelName ?? data.profile?.modelName,
                    reportedDate: details?.reportedDate,
                    factors: details?.factors,
                    creditAttributes: Array.isArray(report?.creditAttributes) ? report.creditAttributes : undefined,
                });
            } else {
                setSpinwheelCreditScore(null);
            }
        }, (error) => {
            logError(error, { context: 'fetch_spinwheel_credit_score' });
            setSpinwheelCreditScore(null);
        });
    }, [uid]);

    const value = useMemo(() => ({ spinwheelCreditScore }), [spinwheelCreditScore]);

    return (
        <CreditScoreContext.Provider value={value}>
            {children}
        </CreditScoreContext.Provider>
    );
};
